import type { ApiClient } from '@commander/api-client';
import { useAuth } from '@commander/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';
import { FEATURE_FLAG_DEFINITIONS } from './constants';
import type { FeatureFlagKey, FeatureFlags } from './types';

/**
 * Generate default feature flag values from definitions
 */
function getDefaultFlags(): FeatureFlags {
  return Object.fromEntries(
    Object.entries(FEATURE_FLAG_DEFINITIONS).map(([key, config]) => [key, config.defaultValue])
  ) as FeatureFlags;
}

/**
 * Context value type for feature flags
 */
interface FeatureFlagContextValue {
  flags: FeatureFlags;
  setFlag: <K extends FeatureFlagKey>(key: K, value: FeatureFlags[K]) => void;
  getFlag: <K extends FeatureFlagKey>(key: K) => FeatureFlags[K];
  isLoading: boolean;
  /**
   * The flags could not be fetched, so `flags` holds the registry defaults
   * rather than the caller's values.
   */
  isError: boolean;
}

/**
 * React Context for feature flags
 */
const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

/**
 * Props for FeatureFlagProvider
 */
interface FeatureFlagProviderProps {
  children: ReactNode;
  apiClient: ApiClient;
}

/**
 * Provider component that manages feature flags via backend API
 *
 * Features:
 * - Fetches flags from backend on mount
 * - Caches flags for 5 minutes
 * - Automatically refetches on window focus
 * - Persists changes to backend database
 * - Synchronized across all user sessions and devices
 *
 * @example
 * ```tsx
 * <FeatureFlagProvider apiClient={apiClient}>
 *   <App />
 * </FeatureFlagProvider>
 * ```
 */
export function FeatureFlagProvider({ children, apiClient }: FeatureFlagProviderProps) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // Fetch flags from backend - ONLY when authenticated. A failed fetch leaves
  // every flag at its default, and a failed refetch keeps the last flags fetched.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: FeatureFlags; success: boolean }>(
        '/feature_flags'
      );
      return response.data;
    },
    enabled: isAuthenticated, // Only fetch when user is logged in
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const flags = data ?? getDefaultFlags();

  // Update flags mutation
  const { mutate: updateFlags } = useMutation({
    mutationFn: async (updates: Partial<FeatureFlags>) => {
      const response = await apiClient.patch<{ data: FeatureFlags; success: boolean }>(
        '/feature_flags',
        updates
      );
      return response.data;
    },
    onSuccess: (newFlags) => {
      // Update the cache with the new flags
      queryClient.setQueryData(['feature-flags'], newFlags);
    },
  });

  /**
   * Set a single feature flag
   * Sends update to backend and updates local cache on success
   */
  const setFlag = <K extends FeatureFlagKey>(key: K, value: FeatureFlags[K]) => {
    updateFlags({ [key]: value } as Partial<FeatureFlags>);
  };

  /**
   * Get a single feature flag value
   */
  const getFlag = <K extends FeatureFlagKey>(key: K): FeatureFlags[K] => {
    return flags[key];
  };

  const value: FeatureFlagContextValue = {
    flags,
    setFlag,
    getFlag,
    isLoading,
    isError: isError && data === undefined,
  };

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

/**
 * Hook to access feature flags from React components
 *
 * @throws Error if used outside of FeatureFlagProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { flags, setFlag, isLoading } = useFeatureFlags();
 *
 *   if (isLoading) return <Spinner />;
 *
 *   if (flags.v2) {
 *     return <V2Component />;
 *   }
 *
 *   return <LegacyComponent />;
 * }
 * ```
 */
export function useFeatureFlags(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagProvider. ' +
        'Make sure your component tree is wrapped with <FeatureFlagProvider apiClient={apiClient}>.'
    );
  }

  return context;
}

/**
 * Convenience hook to check a single feature flag
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isV2Enabled = useFeatureFlag('v2');
 *
 *   if (isV2Enabled) {
 *     return <V2Component />;
 *   }
 *
 *   return <LegacyComponent />;
 * }
 * ```
 */
export function useFeatureFlag<K extends FeatureFlagKey>(key: K): FeatureFlags[K] {
  const { getFlag } = useFeatureFlags();
  return getFlag(key);
}
